import { useGridApiRef } from '@mui/x-data-grid-premium';
import { Metadata } from '@redwoodjs/web';
import { useMemo } from 'react';
import Money from 'shared/utils/Money';
import Link from 'src/components/atoms/Link';
import StatusBadge from 'src/components/atoms/StatusBadge';
import { financialEntityStatuses } from 'src/components/atoms/StatusStepper';
import { PageBody } from 'src/components/containers/PageBody';
import { PageHeader, PageType } from 'src/components/containers/PageHeader';
import useGridExport from 'src/hooks/useGridExport';
import { appLinks } from 'src/lib/routing';
import { createViewConfigStore, VIEW_PAGES } from 'src/managers/viewConfig.store';
import { DataGrid, GridColObj, GridToolbarQuickFilter, GridToolbarV2, useGridRows } from 'src/modules/grids';
import { StoreStateProvider } from 'src/providers/StoreStateProvider';
import { dateCellFormatter, dateGetter, formatCurrency } from 'src/utils/grid/cell/cellFormatters';

import { useCustomerPortalInvoicesGrid } from './useCustomerPortalInvoicesGrid';

const STORAGE_KEY = 'customer-invoices';

function InvoiceLinkCell({ slug }: { slug: string }) {
  return <Link {...appLinks.viewCustomerPortalInvoice({ slug })} label={`#${slug}`} />;
}

function OrderLinkCell({ slug }: { slug: string }) {
  return <Link {...appLinks.editCustomerPortalPurchaseOrder({ slug })} label={`PO #${slug}`} />;
}

const columns: GridColObj = {
  slug: { headerName: 'Invoice #', flex: 1, renderCell: ({ value }) => <InvoiceLinkCell slug={value} /> },
  status: {
    headerName: 'Status',
    flex: 1,
    minWidth: 110,
    getFilterSelectOptions: async () => financialEntityStatuses,
    renderCell: ({ value }) => <StatusBadge status={value} context="invoice" />,
  },
  invoiceDate: {
    headerName: 'Invoice date',
    flex: 1,
    type: 'date',
    valueGetter: (value) => dateGetter(value),
    valueFormatter: dateCellFormatter,
  },
  orderShipDate: {
    headerName: 'PO ship date',
    flex: 1,
    type: 'date',
    valueGetter: (value) => dateGetter(value),
    valueFormatter: dateCellFormatter,
  },
  dueDate: {
    headerName: 'Due date',
    flex: 1,
    type: 'date',
    valueGetter: (value) => dateGetter(value),
    valueFormatter: dateCellFormatter,
  },
  orderSlug: { headerName: 'Order #', flex: 1, renderCell: ({ value }) => <OrderLinkCell slug={value} /> },
  totalPaid: {
    headerName: 'Paid amount',
    flex: 1,
    type: 'number',
    serverFilterValueTransform: { to: (value) => Money.toCents(value), from: (value) => Money.toDollars(value) },
    valueFormatter: (value, row) =>
      typeof value === 'number'
        ? formatCurrency({ value: Money.toDollars(value), currency: row.currencyCode || 'USD' })
        : '--',
  },
  amountRemaining: {
    headerName: 'Remaining amount',
    flex: 1,
    type: 'number',
    serverFilterValueTransform: { to: (value) => Money.toCents(value), from: (value) => Money.toDollars(value) },
    valueFormatter: (value, row) =>
      typeof value === 'number'
        ? formatCurrency({ value: Money.toDollars(value), currency: row.currencyCode || 'USD' })
        : '--',
  },
  totalAmountDue: {
    headerName: 'Total',
    flex: 1,
    type: 'number',
    serverFilterValueTransform: { to: (value) => Money.toCents(value), from: (value) => Money.toDollars(value) },
    valueFormatter: (value, row) =>
      typeof value === 'number'
        ? formatCurrency({ value: Money.toDollars(value), currency: row.currencyCode || 'USD' })
        : '--',
  },
};

const gridQuickFilters: GridToolbarQuickFilter[] = [
  { label: 'All', filters: [{ field: 'status', where: [{ not: 'DRAFT' }] }] },
  { label: 'Open', filters: [{ field: 'status', where: [{ equals: 'OPEN' }] }] },
  { label: 'Paid partial', filters: [{ field: 'status', where: [{ equals: 'PAID_PARTIAL' }] }] },
  { label: 'Paid full', filters: [{ field: 'status', where: [{ equals: 'PAID_FULL' }] }] },
  { label: 'Cancelled', filters: [{ field: 'status', where: [{ equals: 'VOIDED' }] }] },
  { label: 'Finalized', filters: [{ field: 'status', where: [{ equals: 'FINALIZED' }] }] },
];

export default function CustomerPortalInvoicesPage() {
  const apiRef = useGridApiRef();
  const enhancedGridState = useGridRows({
    mode: 'paged',
    storageKey: STORAGE_KEY,
    quickFilterOptions: gridQuickFilters,
  });
  const { rows, loading, pageInfo, totalCount, fetchAllForExport } = useCustomerPortalInvoicesGrid(enhancedGridState);

  useGridExport(apiRef, 'invoices', undefined, undefined, fetchAllForExport);

  const toolbar = useMemo(
    () => (
      <GridToolbarV2
        apiRef={apiRef}
        enhancedGridState={enhancedGridState}
        quickFilterCountModel="ViewBill.Invoice"
        viewPage={VIEW_PAGES.CUSTOMER_PORTAL_PUCHASE_ORDERS}
      />
    ),
    [apiRef, enhancedGridState]
  );

  return (
    <>
      <Metadata title="Invoices" />
      <PageHeader pageType={PageType.CustomerPortalInvoices} />
      <PageBody>
        <StoreStateProvider initializeStore={createViewConfigStore(VIEW_PAGES.CUSTOMER_PORTAL_INVOICES)}>
          <DataGrid
            mode="paged"
            enhancedGridState={enhancedGridState}
            totalCount={totalCount}
            pageInfo={pageInfo}
            loading={loading}
            rows={rows}
            gridKey="customer-portal.invoices.list"
            storageKey={STORAGE_KEY}
            columns={columns}
            apiRef={apiRef}
            slots={{ toolbar }}
          />
        </StoreStateProvider>
      </PageBody>
    </>
  );
}
